import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Checkbox, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { FundOpportunityChatResponseAddress } from 'FundOpportunityChatResponse';
import React from 'react';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';

interface PopulatedFundOpportunityAddressRowsProps {
  addresses: FundOpportunityChatResponseAddress[];
  displayedFieldLines: Record<string, string>;
  animatedKeys: Set<string>;
  selectedAddressIndices: Set<number>;
  onSelectAddress: (index: number) => void;
  onSelectAll: () => void;
  onDeleteSelected: () => void;
  onRemoveAddress?: (addressIndex: number) => void;
  onRemoveAddresses?: (addressIndices: number[]) => void;
}

export const PopulatedFundOpportunityAddressRows: React.FC<
  PopulatedFundOpportunityAddressRowsProps
> = ({
  addresses,
  displayedFieldLines,
  animatedKeys,
  selectedAddressIndices,
  onSelectAddress,
  onSelectAll,
  onDeleteSelected,
  onRemoveAddress,
  onRemoveAddresses,
}) => {
  const allSelected = addresses.length > 0 && selectedAddressIndices.size === addresses.length;
  const someSelected =
    selectedAddressIndices.size > 0 && selectedAddressIndices.size < addresses.length;

  return (
    <>
      {selectedAddressIndices.size > 0 && onRemoveAddresses && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onSelectAll}
                size="small"
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: Palette.tone1.text }}>
                  {selectedAddressIndices.size} address
                  {selectedAddressIndices.size !== 1 ? 'es' : ''} selected
                </Typography>
              </Box>
              <StyledButton
                onClick={onDeleteSelected}
                sx={{
                  backgroundColor: Palette.danger.background,
                  color: Palette.danger.text,
                  '&:hover': {
                    backgroundColor: Palette.danger.activeBg,
                  },
                }}
              >
                Delete Selected
              </StyledButton>
            </Box>
          </TableCell>
        </TableRow>
      )}
      {addresses.map((address, index) => {
        const addressKey = `address-${index}-${address.placeId}`;
        const fullLine = displayedFieldLines[addressKey];
        const isAnimating = animatedKeys.has(addressKey);
        const isSelected = selectedAddressIndices.has(index);

        return (
          <TableRow
            key={addressKey}
            sx={{
              backgroundColor: isSelected
                ? Palette.tone1.activeBg
                : index % 2 === 0
                  ? Palette.tone1.background
                  : 'white',
              '&:hover': { backgroundColor: Palette.tone1.activeBg },
            }}
          >
            <TableCell sx={{ width: 48 }}>
              <Checkbox checked={isSelected} onChange={() => onSelectAddress(index)} size="small" />
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: Palette.tone1.text }}>
              {index === 0 ? 'Addresses' : ''}
            </TableCell>
            <TableCell
              sx={{
                color: Palette.tone1.text,
                opacity: isAnimating ? 0.7 : 1,
                transition: 'opacity 0.3s ease',
              }}
            >
              {fullLine || address.fullAddress}
            </TableCell>
            <TableCell align="right">
              {onRemoveAddress && (
                <IconButton
                  size="small"
                  onClick={() => onRemoveAddress(index)}
                  sx={{ color: Palette.warning.buttonBg }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
};
